import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@utils/cn';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-[color,background-color,border-color,transform] duration-fast ease-out active:scale-[0.97] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 disabled:active:scale-100 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        /**
         * The acid-lime action. Hard rule: at most one visible instance per
         * view — it is a flashlight, not a paint colour.
         */
        primary:
          'bg-primary text-primary-foreground shadow-cta hover:bg-primary/90',
        /** Neutral fallback. Buttons must opt in to lime explicitly. */
        default:
          'border border-border bg-transparent text-secondary-foreground hover:bg-accent hover:text-accent-foreground',
        /** Inverted pill. Marketing nav CTA only. */
        secondary:
          'rounded-full bg-foreground text-background hover:bg-foreground/90',
        outline:
          'border border-border bg-transparent text-secondary-foreground hover:bg-accent hover:text-accent-foreground',
        ghost:
          'text-secondary-foreground hover:bg-accent hover:text-accent-foreground',
        destructive:
          'border border-destructive/40 bg-transparent text-destructive hover:bg-destructive/10',
        link: 'text-secondary-foreground underline-offset-4 hover:underline'
      },
      size: {
        default: 'h-9 px-4 py-2',
        sm: 'h-8 rounded-md px-3 text-caption',
        lg: 'h-10 rounded-md px-8',
        icon: 'h-9 w-9'
      }
    },
    defaultVariants: {
      variant: 'default',
      size: 'default'
    }
  }
);

export interface ButtonProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = 'Button';

export { Button, buttonVariants };
